import Location from './Location';

export enum CreditCardTypeId {
    Mastercard = 5,
    Visa = 6,
    AmericanExpress = 7,
    Discover = 8,
    Diners = 9,
}

export enum CreditCardCsvSize {
    Mastercard = 3,
    Visa = 3,
    AmericanExpress = 4,
    Discover = 3,
    Diners = 3,
}

export type CreditCardBrand =
  | 'Visa'
  | 'Master Card'
  | 'American Express'
  | 'BPAY' // Discover?
  | 'Diners Club';

export interface CreditCard {
    name: string;
    number: string;
    expiryMonth: string;
    expiryYear: string;
    cvv: string;
    typeId: CreditCardTypeId;
}

/**
 * Uses the credit card number to determine which type of credit card it is
 *
 * @param cc
 * @returns number | undefined
 */
export function creditCardType(cc: string): CreditCardTypeId | undefined {
    const amex = new RegExp('^3[47][0-9]{13}$');
    const visa = new RegExp('^4[0-9]{12}(?:[0-9]{3})?$');

    const mastercard = new RegExp('^5[1-5][0-9]{14}$');
    const mastercard2 = new RegExp('^2[2-7][0-9]{14}$');

    const disco1 = new RegExp('^6011[0-9]{12}[0-9]*$');
    const disco2 = new RegExp('^62[24568][0-9]{13}[0-9]*$');
    const disco3 = new RegExp('^6[45][0-9]{14}[0-9]*$');

    const diners = new RegExp('^3[0689][0-9]{12}[0-9]*$');

    if (visa.test(cc)) {
        return CreditCardTypeId.Visa;
    }
    if (amex.test(cc)) {
        return CreditCardTypeId.AmericanExpress;
    }
    if (mastercard.test(cc) || mastercard2.test(cc)) {
        return CreditCardTypeId.Mastercard;
    }
    if (disco1.test(cc) || disco2.test(cc) || disco3.test(cc)) {
        return CreditCardTypeId.Discover;
    }
    if (diners.test(cc)) {
        return CreditCardTypeId.Diners;
    }

    return undefined;
}

/**
 * Validates a credit card number based on the location's allowed credit card types
 *
 * @param number
 * @param location
 * @returns boolean
 */
export function validateCreditCardNumber(number: string, location: Location): boolean {
    const allowedCreditCards: CreditCardTypeId[] = [];

    if (location.rapidstor.allowedCreditCardTypes.visa) {
        allowedCreditCards.push(CreditCardTypeId.Visa);
    }
    if (location.rapidstor.allowedCreditCardTypes.mastercard) {
        allowedCreditCards.push(CreditCardTypeId.Mastercard);
    }
    if (location.rapidstor.allowedCreditCardTypes.amex) {
        allowedCreditCards.push(CreditCardTypeId.AmericanExpress);
    }

    const ccType = creditCardType(number);

    if (!ccType) {
        return false;
    }

    if (!allowedCreditCards.includes(ccType)) {
        return false;
    }

    return true;
}

/**
 * Validates a credit card csv against the credit card type and csv size enums
 *
 * @param type
 * @param csv
 * @returns boolean
 */
export function validateCreditCardCSV(type: number, csv: string): boolean {
    switch (type) {
        case CreditCardTypeId.Mastercard:
            return csv.length === CreditCardCsvSize.Mastercard;
        case CreditCardTypeId.Visa:
            return csv.length === CreditCardCsvSize.Visa;
        case CreditCardTypeId.AmericanExpress:
            return csv.length === CreditCardCsvSize.AmericanExpress;
        case CreditCardTypeId.Discover:
            return csv.length === CreditCardCsvSize.Discover;
        case CreditCardTypeId.Diners:
            return csv.length === CreditCardCsvSize.Diners;
        default:
            return false;
    }
}
